import React from "react";
import "./CodeSyntaxHighlighter.css";

interface CodeSyntaxHighlighterProps {
  code: string;
  language?: string;
}

const PYTHON_KEYWORDS = new Set([
  "def",
  "class",
  "return",
  "if",
  "elif",
  "else",
  "while",
  "for",
  "in",
  "import",
  "from",
  "as",
  "try",
  "except",
  "finally",
  "raise",
  "with",
  "is",
  "not",
  "and",
  "or",
  "lambda",
  "pass",
  "break",
  "continue",
  "yield",
  "global",
  "nonlocal",
  "async",
  "await",
  "assert",
]);

const PYTHON_BUILTINS = new Set([
  "int",
  "str",
  "float",
  "bool",
  "list",
  "dict",
  "set",
  "tuple",
  "print",
  "len",
  "range",
  "input",
  "max",
  "min",
  "sum",
  "abs",
  "open",
  "super",
  "self",
  "True",
  "False",
  "None",
  "__name__",
  "__main__",
]);

const SQL_KEYWORDS = new Set([
  "SELECT",
  "FROM",
  "WHERE",
  "JOIN",
  "LEFT",
  "RIGHT",
  "INNER",
  "OUTER",
  "CROSS",
  "ON",
  "GROUP",
  "BY",
  "ORDER",
  "HAVING",
  "LIMIT",
  "OFFSET",
  "INSERT",
  "INTO",
  "UPDATE",
  "DELETE",
  "CREATE",
  "TABLE",
  "DROP",
  "ALTER",
  "ADD",
  "AS",
  "AND",
  "OR",
  "NOT",
  "IN",
  "IS",
  "NULL",
  "LIKE",
  "ILIKE",
  "BETWEEN",
  "EXISTS",
  "CASE",
  "WHEN",
  "THEN",
  "ELSE",
  "END",
  "UNION",
  "ALL",
  "DISTINCT",
  "WITH",
]);

const SQL_FUNCTIONS = new Set([
  "COUNT",
  "SUM",
  "AVG",
  "MAX",
  "MIN",
  "COALESCE",
  "CONCAT",
  "NOW",
  "DATE_TRUNC",
  "ROUND",
  "UPPER",
  "LOWER",
  "SUBSTRING",
  "TRIM",
  "CAST",
]);

export const CodeSyntaxHighlighter: React.FC<CodeSyntaxHighlighterProps> = ({
  code,
  language = "code",
}) => {
  if (!code || typeof code !== "string") {
    return <span className="syn-comment">// No code submitted</span>;
  }

  const lang = (language || "").toLowerCase();
  const isSql = lang.includes("sql");

  const tokenizePythonLine = (line: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    let idx = 0;

    while (idx < line.length) {
      // 1. Comments
      if (line[idx] === "#") {
        nodes.push(
          <span key={idx} className="syn-comment">
            {line.slice(idx)}
          </span>,
        );
        break;
      }

      // 2. Double-quoted strings
      if (line[idx] === '"') {
        let end = idx + 1;
        while (
          end < line.length &&
          (line[end] !== '"' || line[end - 1] === "\\")
        ) {
          end++;
        }
        if (end < line.length) end++;
        nodes.push(
          <span key={idx} className="syn-string">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // 3. Single-quoted strings
      if (line[idx] === "'") {
        let end = idx + 1;
        while (
          end < line.length &&
          (line[end] !== "'" || line[end - 1] === "\\")
        ) {
          end++;
        }
        if (end < line.length) end++;
        nodes.push(
          <span key={idx} className="syn-string">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // 4. Numbers
      if (
        /\d/.test(line[idx]) &&
        (idx === 0 || /[\s\(\)\[\],:=%+\-*/<>]/.test(line[idx - 1]))
      ) {
        let end = idx;
        while (end < line.length && /[\d\.]/.test(line[end])) {
          end++;
        }
        nodes.push(
          <span key={idx} className="syn-number">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // 5. Identifiers / Keywords / Functions
      if (/[a-zA-Z_]/.test(line[idx])) {
        let end = idx;
        while (end < line.length && /[a-zA-Z0-9_]/.test(line[end])) {
          end++;
        }
        const word = line.slice(idx, end);
        const lineBefore = line.slice(0, idx).trim();

        if (lineBefore.endsWith("def") || lineBefore.endsWith("class")) {
          nodes.push(
            <span key={idx} className="syn-function">
              {word}
            </span>,
          );
        } else if (PYTHON_KEYWORDS.has(word)) {
          nodes.push(
            <span key={idx} className="syn-keyword">
              {word}
            </span>,
          );
        } else if (PYTHON_BUILTINS.has(word)) {
          nodes.push(
            <span key={idx} className="syn-builtin">
              {word}
            </span>,
          );
        } else if (end < line.length && line[end] === "(") {
          nodes.push(
            <span key={idx} className="syn-function">
              {word}
            </span>,
          );
        } else {
          nodes.push(
            <span key={idx} className="syn-variable">
              {word}
            </span>,
          );
        }
        idx = end;
        continue;
      }

      // 6. Operators / Symbols
      if (/[=+\-%*\/<>!:&|~^]/.test(line[idx])) {
        let end = idx;
        while (end < line.length && /[=+\-%*\/<>!:&|~^]/.test(line[end])) {
          end++;
        }
        nodes.push(
          <span key={idx} className="syn-operator">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // Punctuation & Whitespace
      const char = line[idx];
      if (/[()\[\]{},;:]/.test(char)) {
        nodes.push(
          <span key={idx} className="syn-punctuation">
            {char}
          </span>,
        );
      } else {
        nodes.push(char);
      }
      idx++;
    }

    return nodes;
  };

  const tokenizeSqlLine = (line: string): React.ReactNode[] => {
    const nodes: React.ReactNode[] = [];
    let idx = 0;

    while (idx < line.length) {
      // 1. Comments --
      if (line.slice(idx, idx + 2) === "--") {
        nodes.push(
          <span key={idx} className="syn-comment">
            {line.slice(idx)}
          </span>,
        );
        break;
      }

      // 2. Single-quoted strings
      if (line[idx] === "'") {
        let end = idx + 1;
        while (end < line.length && line[end] !== "'") {
          end++;
        }
        if (end < line.length) end++;
        nodes.push(
          <span key={idx} className="syn-string">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // 3. Double-quoted strings
      if (line[idx] === '"') {
        let end = idx + 1;
        while (end < line.length && line[end] !== '"') {
          end++;
        }
        if (end < line.length) end++;
        nodes.push(
          <span key={idx} className="syn-string">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // 4. Numbers
      if (
        /\d/.test(line[idx]) &&
        (idx === 0 || /[\s\(\)\[\],:=+*\/>]/.test(line[idx - 1]))
      ) {
        let end = idx;
        while (end < line.length && /[\d\.]/.test(line[end])) {
          end++;
        }
        nodes.push(
          <span key={idx} className="syn-number">
            {line.slice(idx, end)}
          </span>,
        );
        idx = end;
        continue;
      }

      // 5. Identifiers / Keywords / Functions
      if (/[a-zA-Z_]/.test(line[idx])) {
        let end = idx;
        while (end < line.length && /[a-zA-Z0-9_\.]/.test(line[end])) {
          end++;
        }
        const word = line.slice(idx, end);
        const upperWord = word.toUpperCase();

        if (SQL_KEYWORDS.has(upperWord)) {
          nodes.push(
            <span key={idx} className="syn-keyword sql">
              {word}
            </span>,
          );
        } else if (SQL_FUNCTIONS.has(upperWord)) {
          nodes.push(
            <span key={idx} className="syn-function sql">
              {word}
            </span>,
          );
        } else {
          nodes.push(
            <span key={idx} className="syn-variable sql">
              {word}
            </span>,
          );
        }
        idx = end;
        continue;
      }

      // 6. Operators / Punctuation
      const char = line[idx];
      if (/[=+\-%*\/<>!;,.]/.test(char)) {
        nodes.push(
          <span key={idx} className="syn-operator">
            {char}
          </span>,
        );
      } else if (/[()]/.test(char)) {
        nodes.push(
          <span key={idx} className="syn-punctuation">
            {char}
          </span>,
        );
      } else {
        nodes.push(char);
      }
      idx++;
    }

    return nodes;
  };

  const lines = code.split("\n");

  return (
    <code>
      {lines.map((line, lIdx) => (
        <div key={lIdx} className="syn-line">
          {isSql ? tokenizeSqlLine(line) : tokenizePythonLine(line)}
        </div>
      ))}
    </code>
  );
};

export default CodeSyntaxHighlighter;
